/**
 * Card Component
 *
 * A flexible card component with multiple variants for displaying content.
 * Follows clean, minimalistic design principles.
 */

import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/cn';
import type { CardProps, CardHeaderProps, CardBodyProps, CardFooterProps } from './card.types';

const cardVariants = cva('transition-all duration-200', {
  variants: {
    variant: {
      default: 'bg-white border border-gray-200',
      outlined: 'bg-transparent border-2 border-gray-300',
      elevated: 'bg-white shadow-md hover:shadow-lg',
    },
    // Phase 4.4c-1 — `radius` is opt-in. The previous default was the
    // bare `rounded-xl` (12px). We preserve that as the default here
    // (`'xl'`) so existing consumers see no change. `'lg'` (8px) is
    // available when a tighter radius matches the design.
    radius: {
      lg: 'rounded-lg',
      xl: 'rounded-xl',
    },
    hoverable: {
      true: 'hover:shadow-lg hover:-translate-y-0.5',
      false: '',
    },
    clickable: {
      true: 'cursor-pointer',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    radius: 'xl',
    hoverable: false,
    clickable: false,
  },
});

/**
 * Card - Main container component
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, radius, hoverable, clickable, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(cardVariants({ variant, radius, hoverable, clickable }), className)} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * CardHeader - Header section of the card
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('p-6 pb-4', className)} {...props}>
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * CardBody - Main content section of the card
 */
export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props}>
      {children}
    </div>
  );
});

CardBody.displayName = 'CardBody';

/**
 * CardFooter - Footer section of the card
 */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('p-6 pt-4 border-t border-gray-200', className)} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';
